import { Link } from 'react-router-dom';
import { ChevronRight, Heart, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AboutPage = () => {
  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      {/* Hero */}
      <section className="chaleur-container mb-24">
        <div className="grid md:grid-cols-2 gap-12 md:gap-24 items-center">
          <div>
            <p className="caption text-primary mb-4">Our Story</p>
            <h1 className="text-4xl md:text-5xl font-light mb-6">
              The Story of <span className="editorial-italic">Warmth</span> and Giving
            </h1>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Chaleur—French for warmth—was born from a simple yet profound belief: 
              that true ownership lies not in what we possess, but in what we give to others. 
              Every thread we weave carries this philosophy, connecting the skilled hands of 
              Indian artisans to wardrobes around the world.
            </p>
          </div>
          <div className="aspect-[4/5] overflow-hidden">
            <img 
              src="/images/products/santorini_collection.jpg"
              alt="Chaleur collection overlooking Santorini"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Video Hero Section */}
      <section className="relative h-[60vh] mb-24 overflow-hidden">
        <video 
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay 
          muted 
          loop 
          playsInline
        >
          <source src="/videos/artisan_craft_3.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full flex items-center justify-center text-center text-white">
          <div>
            <p className="caption text-white/80 mb-4">Handcrafted with Love</p>
            <h2 className="text-4xl md:text-5xl font-light">Every Thread Tells a Story</h2>
          </div>
        </div>
      </section>

      {/* The Meaning */}
      <section className="section-spacing bg-secondary/30">
        <div className="chaleur-container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-accent text-3xl text-primary mb-6">Chaleur</p>
            <h2 className="text-3xl md:text-4xl font-light mb-6">The Meaning Behind the Name</h2>
            <p className="text-muted-foreground leading-relaxed">
              The French word "chaleur" means warmth—the gentle, embracing kind that 
              makes you feel at home. It's the warmth of a handwoven shawl on a cold evening, 
              the warmth of giving a meaningful gift, and the warmth shared between artisan 
              and wearer across continents.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-primary mb-4">Our Values</p>
            <h2 className="text-3xl md:text-4xl font-light">What We Stand For</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-primary/20 flex items-center justify-center">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl mb-3">Generosity</h3>
              <p className="text-sm text-muted-foreground">
                We believe in the power of giving and the profound impact it has on both giver and receiver.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-primary/20 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl mb-3">Kindness</h3>
              <p className="text-sm text-muted-foreground">
                Kindness is at the heart of everything we do—from artisan partnerships to customer care.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-primary/20 flex items-center justify-center">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl mb-3">Connection</h3>
              <p className="text-sm text-muted-foreground">
                We foster meaningful connections—between cultures, between hands that make and hearts that receive.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-primary/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <h3 className="text-xl mb-3">Craftsmanship</h3>
              <p className="text-sm text-muted-foreground">
                Every piece is a testament to skill passed down through generations, preserving heritage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founders Note */}
      <section className="section-spacing bg-foreground text-primary-foreground">
        <div className="chaleur-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="aspect-square overflow-hidden">
              <img 
                src="/images/products/artisan_yarn_hands.jpg"
                alt="Artisan hands with yarn - the heart of Chaleur"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="caption text-white/60 mb-4">Founder's Note</p>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Why We Started Chaleur
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">
                "We started Chaleur with a question: Why settle for ordinary when extraordinary awaits? 
                This question fueled our pursuit of connecting the world's finest artisans with 
                discerning individuals who value craftsmanship, purpose, and the art of giving."
              </p>
              <p className="text-white/70 leading-relaxed mb-6">
                "Every scarf, wrap, and drape carries not just warmth, but stories—of the hands 
                that wove them, the traditions they preserve, and the connections they create 
                when they're given and received."
              </p>
              <p className="font-accent text-2xl text-white/80">— The Chaleur Team</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="text-center mb-12">
            <p className="caption text-primary mb-4">Our World</p>
            <h2 className="text-3xl md:text-4xl font-light">Chaleur Around the Globe</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="aspect-square overflow-hidden">
              <img src="/images/products/santorini_gratitude.jpg" alt="Chaleur in Santorini" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden">
              <img src="/images/products/desert_scarf.jpg" alt="Chaleur in desert" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden">
              <img src="/images/products/autumn_leaves.jpg" alt="Chaleur in autumn" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden">
              <img src="/images/products/beach_with_love.jpg" alt="Chaleur at beach" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
            </div>
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="caption text-primary mb-4">Our Promise</p>
            <h2 className="text-3xl md:text-4xl font-light mb-6">
              Embrace Giving, Embody Ownership
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              We promise to be a source of inspiration, reminding you that true fulfillment 
              comes from giving to others. By embodying the philosophy that the only thing 
              that truly belongs to us is what we give, Chaleur invites you to embrace a 
              lifestyle of generosity and compassion.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="btn-chaleur-primary">
                <Link to="/shop">Explore Collection</Link>
              </Button>
              <Button asChild variant="outline" className="btn-chaleur-secondary">
                <Link to="/impact">See Our Impact</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
